"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-9 w-20 bg-white/20 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-white/20 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-white/20 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-primary hover:text-primary/80 transition-colors relative group px-3 py-2 font-default font-bold uppercase tracking-wider">
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-destructive group-hover:w-full transition-all duration-300" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2">
        <div className="h-8 bg-white/20 animate-pulse rounded-md" />
        <div className="h-8 bg-white/20 animate-pulse rounded-md" />
      </div>;
  }
  return <div className="space-y-2">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block px-4 py-2 text-primary hover:bg-primary/5 font-default font-bold uppercase rounded-md transition-colors">
          {item.label}
        </Link>)}
    </div>;
}
function MainNavigationContent() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className="sticky top-0 z-40 w-full transition-all duration-300 bg-white text-primary shadow-sm">
      <div className="container mx-auto px-8 py-4">
        <div className="flex items-center justify-between gap-4 lg:gap-8">
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex-shrink-0 -ml-6">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673119/img/Untitled (Website) (3).png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673119/img/Untitled (Website) (3).png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673119/img/Untitled (Website) (3).png-wvc-sizes" alt="Kimball Speedy Lube & Tires" className="w-52 h-auto" loading="lazy" decoding="async" />
            </Link>
            <div className="hidden sm:block border-l border-primary/20 h-10 mx-4" />
            <span className="block sm:hidden text-primary font-default font-medium text-sm uppercase tracking-widest">Fast &amp; FRiendly<br />&nbsp;Auto Service</span>
          </div>

          <div className="hidden lg:flex items-center flex-1 justify-center">
            <MenuProvider menu_id="16">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden lg:block w-48">
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search services..." className="bg-muted rounded-md border-input text-primary placeholder:text-primary/60" />
            </div>

            <Button variant="default" size="default" className="hidden md:flex bg-primary text-primary-foreground hover:bg-primary/90 font-default font-bold uppercase rounded-md shadow-md" asChild>
              <Link to="tel:4238371807" className="text-primary-foreground">
                Call Now
              </Link>
            </Button>

            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-primary hover:bg-primary/5">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-white border-border text-primary">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <Link to="/">
                        <div className="p-1.5 rounded-md">
                        </div>
                      </Link>
                    </div>

                    <MenuProvider menu_id="16">
                      <MobileMenuConsumer />
                    </MenuProvider>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export default function MainNavigation() {
  return <MainNavigationContent />;
}